/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// Cart JSON and section HTML use different endpoints. Keep the full cart
// payload for Dawn's pubsub subscribers, and let the native drawer render
// its own sections after those subscribers finish.
async function fetchCartRefreshJSON(url) {
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
  return response.json();
}

async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const drawer = document.querySelector('cart-drawer');
  const drawerSections =
    typeof drawer?.renderContents === 'function' ? drawer.getSectionsToRender() : [];
  const drawerSectionIds = new Set(drawerSections.map((section) => section.id));
  const sectionIds = new Set([...sections.keys(), ...drawerSectionIds]);
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const [cartData, sectionHTML] = await Promise.all([
    fetchCartRefreshJSON(`${cartUrl}.js`),
    sectionIds.size
      ? fetchCartRefreshJSON(`${cartUrl}?sections=${encodeURIComponent([...sectionIds].join(','))}`)
      : Promise.resolve({}),
  ]);

  if (!cartData || !Array.isArray(cartData.items) || typeof cartData.item_count !== 'number') {
    throw new Error('Cart refresh returned an invalid cart payload');
  }

  // Validate all fragments before replacing anything. A failed section
  // response must reach the existing reload fallback instead of leaving
  // the cart count or drawer silently stale.
  const parsedSections = new Map();
  for (const id of sectionIds) {
    const html = sectionHTML?.[id];
    if (typeof html !== 'string' || !html) throw new Error(`Cart section unavailable: ${id}`);
    parsedSections.set(id, new DOMParser().parseFromString(html, 'text/html'));
  }
  for (const section of drawerSections) {
    if (!parsedSections.get(section.id).querySelector(section.selector || '.shopify-section')) {
      throw new Error(`Cart section content unavailable: ${section.id}`);
    }
  }
  for (const [id, { mount, extractSelector }] of sections) {
    if (drawerSectionIds.has(id)) continue;
    const source = parsedSections.get(id).querySelector(extractSelector);
    if (!source) throw new Error(`Cart section content unavailable: ${id}`);
    mount.replaceChildren(...source.childNodes);
  }

  // Await subscribers before the final native drawer render so their
  // partial refresh cannot overwrite the freshly rendered drawer.
  await publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: { ...cartData, sections: sectionHTML },
  });

  if (drawerSections.length) {
    drawer.classList.toggle('is-empty', cartData.item_count === 0);
    drawer.renderContents({ ...cartData, sections: sectionHTML });
  }
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
